import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, ChangeDetectorRef } from '@angular/core';
import { AzureAuthService } from '../../services/azure-auth.service';
import { StackDirectives } from 'src/app/shared/directives/stack-directives';
import { Router } from '@angular/router';
import { SStorageService } from 'src/app/shared/services/sessionStorage.service';
import { AuthService } from '../../services/auth.service';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ToastrService } from 'ngx-toastr';
import { CLAVE_URL_TRAS_LOGIN, esUrlTrasLoginPermitida } from 'src/app/shared/guards/token.guard';

@Component({
  selector: 'app-login-azure',
  standalone: true,
  imports: [CommonModule, StackDirectives],
  template: `
    <div class="login-container">
      @if (!username) {
        <h2>Iniciar sesión con Azure</h2>
        <p class="my-0">
          Para acceder a la aplicación, por favor inicia sesión con tu cuenta de Azure. Si no tienes una cuenta, por favor contacta al
          administrador del sistema.
        </p>
        <p class="my-0">Una vez que inicies sesión, podrás acceder a todas las funcionalidades de la aplicación.</p>

        <!-- Mensaje de error -->
        @if (errorMessage) {
          <div class="error-message">
            <p>{{ errorMessage }}</p>
          </div>
        }

        <div class="flex justify-content-end gap-2 w-full">
          <button miButton="back" (click)="goBack()" [disabled]="isLoading">Atrás</button>
          <button miButton="primary" (click)="login()" [disabled]="isLoading">
            @if (isLoading) {
              <span>Cargando...</span>
            } @else {
              <span>Iniciar sesión con Azure</span>
            }
          </button>
        </div>
      }

      @if (username) {
        <div class="success-container">
          <h2>¡Bienvenido!</h2>
          <div>
            <p class="user-welcome">
              Hola, <strong>{{ username }}</strong>
            </p>
            <p>Has iniciado sesión correctamente. Si no fuiste redirigido al portal, haz clic en el botón de Ir al portal.</p>
          </div>
          <div class="buttons-container">
            <button miButton="back" (click)="logout()">Cerrar sesión</button>
            <button miButton="primary" (click)="handleLoginSuccess()">Ir al portal</button>
          </div>
        </div>
      }
    </div>
  `,
  styles: [
    `
      .login-container {
        max-width: 500px;
        margin: 0 auto;
        padding: 30px;
        display: flex;
        flex-direction: column;
        align-items: start;
        justify-content: space-between;
        height: 300px;
      }
      .error-message {
        background-color: #ffeeee;
        border-left: 4px solid #f44336;
        padding: 12px;
        margin: 20px 0;
        border-radius: 4px;
      }
      .success-container {
        text-align: center;
        height: 300px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-between;
      }
      .user-welcome {
        font-size: 1.2rem;
        margin: 16px 0;
      }
      .buttons-container {
        display: flex;
        gap: 10px;
        justify-content: end;
        margin-top: 20px;
        width: 100%;
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginAzureComponent implements OnInit {
  private azureAuthService = inject(AzureAuthService);
  private sstorageService = inject(SStorageService);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);
  private loader = inject(LoaderService);
  private authService = inject(AuthService);

  username: string | null = null;
  errorMessage: string | null = null;
  isLoading: boolean = false;

  constructor(private toastr: ToastrService) {}

  ngOnInit(): void {
    this.initAuth();
  }

  async initAuth(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = null;
    this.cdr.detectChanges();

    try {
      await this.azureAuthService.init();
      const result = await this.azureAuthService.handleRedirect();
      const account = result?.account || (await this.azureAuthService.getAccount());

      if (account) {
        this.username = account.username ?? account.name ?? 'Usuario';

        // Obtener el access token real y guardarlo
        const accessToken = await this.azureAuthService.getAccessToken();
        if (accessToken) {
          this.sstorageService.setItem('token', accessToken);
          this.sstorageService.setItem('mod', '1'); // Mientras se inicia sesion, se asume que es el módulo 1
        } else {
          this.errorMessage = 'No se pudo obtener el token de acceso de Azure.';
          this.cdr.detectChanges();
          return;
        }

        this.handleLoginSuccess();
      }
    } catch (error: any) {
      console.error('Error al autenticar con Azure:', error);
      this.errorMessage = this.formatErrorMessage(error);
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  goBack() {
    this.router.navigate(['/login']);
  }

  private formatErrorMessage(error: any): string {
    // Extraer mensaje de error más amigable según el tipo de error
    if (error.errorCode === 'user_cancelled') {
      return 'Has cancelado el inicio de sesión. Por favor, intenta nuevamente.';
    } else if (error.errorCode === 'consent_required') {
      return 'Se requiere tu consentimiento para acceder a la aplicación. Por favor, acepta los permisos solicitados.';
    } else if (error.errorDescription) {
      return `Error de autenticación: ${error.errorDescription}`;
    } else {
      return 'Ha ocurrido un error durante la autenticación. Por favor, intenta nuevamente o contacta a soporte.';
    }
  }

  login() {
    this.isLoading = true;
    this.errorMessage = null;
    this.cdr.detectChanges();

    try {
      this.azureAuthService.login();
    } catch (error: any) {
      this.isLoading = false;
      this.errorMessage = this.formatErrorMessage(error);
      this.cdr.detectChanges();
    }
  }

  logout() {
    this.isLoading = true;
    this.azureAuthService.logout().then(() => {
      this.username = null;
      this.isLoading = false;
      this.sstorageService.removeItem('userEmail');
      this.cdr.detectChanges();
    });
  }

  handleLoginSuccess(): void {
    this.loader.show('Cargando información del usuario...'); // Muestra el loader mientras se carga la información del usuario
    const token = this.sstorageService.getItem('token');
    if (!token) {
      this.loader.hide();
      this.router.navigate(['/login']);
      return;
    } 

    this.authService.getInfoUser(token)?.subscribe({
      next: (userInfo) => {
        this.sstorageService.setItem('userInfo', JSON.stringify(userInfo.data));
        this.sstorageService.setItem('msal', true);
        const destino = this.consumirUrlTrasLogin();
        if (destino) {
          this.router.navigateByUrl(destino);
        } else {
          this.router.navigate(['app/mi-portal/inicio']);
        }
      },
      error: (error) => {
        this.loader.hide(); // Oculta el loader en caso de error al obtener la información del usuario
        console.error('Error al obtener información del usuario:', error);
        this.toastr.error('Error al obtener información del usuario', 'Error');
      }
    });
  }

  /**
   * Lee y borra la URL guardada por el TokenGuard. Solo se acepta si empieza EXACTAMENTE por
   * la lista blanca (aprobación TD o aprobación de aceptación de riesgo) y no contiene nada que la saque de la app; cualquier otra
   * cosa (//evil.com, https://..., otras rutas) se ignora.
   */
  private consumirUrlTrasLogin(): string | null {
    let url: string | null = null;
    try {
      url = sessionStorage.getItem(CLAVE_URL_TRAS_LOGIN);
      sessionStorage.removeItem(CLAVE_URL_TRAS_LOGIN);
    } catch {
      return null;
    }
    if (url && esUrlTrasLoginPermitida(url) && !url.includes('//') && !url.includes('\\')) {
      return url;
    }
    return null;
  }
}
